import { Callout, CalloutPlacement, ModeProvider, spaceToPx } from 'canoe';
import React from 'react';

import { NODE_LABEL_CONTAINER_ID } from 'craft/components/NodeLabels/NodeLabel';
import { NO_ACTION } from 'craft/constants';
import { useSelection } from 'craft/hooks';
import { useViewportDom } from 'craft/state/viewport/selectors';

import { ToolbarMenuBodyContainer } from './ToolbarMenuBodyContainer';

export const SelectedToolbarMenuCallout = ({
  dom,
  children,
  calloutPlacement = 'top-start',
  isNodeToolbar,
}: {
  dom: HTMLElement | null;
  children: React.ReactNode;
  calloutPlacement?: CalloutPlacement;
  isNodeToolbar?: boolean;
}) => {
  const { selection } = useSelection();
  const { viewportWrapperEl } = useViewportDom();
  const extraPadding = spaceToPx('xs');

  return (
    <ModeProvider value={{ mode: 'Dark' }}>
      <Callout
        referenceElement={dom}
        modifiers={[
          {
            name: 'flip',
            options: {
              padding: extraPadding,
              boundary: viewportWrapperEl,
            },
          },
          {
            name: 'offset',
            options: {
              offset: ({ placement, reference, popper }) => {
                const nodeLabel = document.getElementById(
                  `${NODE_LABEL_CONTAINER_ID}-${selection.first}`,
                );

                const labelBounds = nodeLabel?.getBoundingClientRect();
                const addExtraOffset =
                  isNodeToolbar &&
                  (reference?.width ?? 0) - (labelBounds?.width ?? 0) <= (popper?.width ?? 0);

                // when toolbar is positioned at the top, take the node label height into consideration when positioning
                if ((/top/.test(placement) && addExtraOffset) || placement === 'top-end') {
                  return [0, (labelBounds?.height ?? 0) + extraPadding];
                }

                return [0, extraPadding];
              },
            },
          },
          {
            name: 'preventOverflow',
            options: {
              padding: extraPadding,
              tetherOffset: -extraPadding,
              boundary: viewportWrapperEl,
            },
          },
        ]}
        placement={calloutPlacement}
      >
        {(calloutProps) => (
          <ToolbarMenuBodyContainer
            data-craft-ignore-events
            data-focus-listener={NO_ACTION}
            ref={calloutProps.ref}
            style={calloutProps.style}
            css={{ zIndex: 2 }}
            {...calloutProps.attributes.popper}
          >
            {children}
          </ToolbarMenuBodyContainer>
        )}
      </Callout>
    </ModeProvider>
  );
};
